import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Select,
  MenuItem,
  Typography,
  Stack,
  FormControl,
  InputLabel,
  CssBaseline,
  useMediaQuery,
  useTheme,
  Checkbox,
  ListItemText,
  Tooltip,
  FormControlLabel,
  TextField,
  InputAdornment,
  Collapse,
  IconButton,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import { DataGridPro, useGridApiRef } from '@mui/x-data-grid-pro';
import { createTheme, ThemeProvider, StyledEngineProvider } from '@mui/material/styles';
import { useStoreData } from '../useStoreData';
import dataStore from '../dataStore';
import { useOddsFormat, fmtPrice } from '../oddsFormat';
import { BOOK_GROUPS, booksInGroup, isGroupValue, groupFromValue, GROUP_PREFIX } from '../bookGroups';
import { useLogoMaps } from '../useLogoMaps';
import OddsHistoryModal, { useOddsHistory } from '../components/OddsHistoryModal';
import MarketsModal, { useMarkets, buildMarkets } from '../components/MarketsModal';

// ══════════════════════════════════════════════════════════
//  FILTER OPTIONS
//  Markets map directly to OpticOdds market base types.
//  Period types are parsed from market_id prefixes by the server.
// ══════════════════════════════════════════════════════════

const defaultMarkets = ['Total', 'Spread', 'Moneyline', 'Summary'];

const marketFilterMap = {
  Total:     'total_points',
  Spread:    'point_spread',
  Moneyline: 'moneyline',
};

// Market equivalences across sports (e.g. NHL puck_line = NBA point_spread)
const SPREAD_MARKETS = new Set(['point_spread', 'puck_line', 'game_spread', 'run_line', 'kills_handicap', 'asian_handicap']);
const TOTAL_MARKETS  = new Set(['total_points', 'total_goals', 'total_games', 'total_runs', 'total_kills', 'total_rounds']);

// Sport-aware market filter: maps display label → market_id per sport
const marketFilterBySport = {
  NHL: { Spread: 'puck_line', Total: 'total_goals', Moneyline: 'moneyline' },
  ATP: { Spread: 'game_spread', Total: 'total_games', Moneyline: 'moneyline' },
  WTA: { Spread: 'game_spread', Total: 'total_games', Moneyline: 'moneyline' },
  MLB: { Spread: 'run_line', Total: 'total_runs', Moneyline: 'moneyline' },
  PGA: { Moneyline: 'moneyline' },
  'League of Legends': { Spread: 'kills_handicap', Total: 'total_kills', Moneyline: 'moneyline' },
  CS2: { Spread: 'kills_handicap', Total: 'total_kills', Moneyline: 'moneyline' },
  UFC: { Total: 'total_rounds', Moneyline: 'moneyline' },
};

const periodTypesBySport = {
  NBA: ['Full Game', 'First Half', 'Second Half', 'First Quarter'],
  CBB: ['Full Game', 'First Half', 'Second Half', 'First Quarter'],
  WNBA: ['Full Game', 'First Half', 'Second Half', 'First Quarter'],
  MLB: ['Full Game'],
  NHL: ['Full Game', 'First Period', 'Second Period', 'Third Period'],
  NFL: ['Full Game', 'First Half', 'Second Half', 'First Quarter', 'Second Quarter', 'Third Quarter', 'Fourth Quarter'],
  CFB: ['Full Game', 'First Half', 'Second Half', 'First Quarter', 'Second Quarter', 'Third Quarter', 'Fourth Quarter'],
  WTA: ['Full Game', 'Set 1', 'Set 2'],
  ATP: ['Full Game', 'Set 1', 'Set 2'],
  'League of Legends': ['Full Game', 'Map 1', 'Map 2', 'Map 3', 'Map 4', 'Map 5'],
  CS2: ['Full Game', 'Map 1', 'Map 2', 'Map 3', 'Map 4', 'Map 5'],
  '__soccer__': ['Full Game', 'First Half'],
};

// Markets that only exist at map/period level (no Full Game equivalent)
const noFullGameMarkets = {
  'League of Legends': new Set(['Total', 'Spread', 'Summary']),
  CS2: new Set(['Total', 'Spread', 'Summary']),
};

const marketsBySport = {
  PGA: ['Moneyline'],
  UFC: ['Moneyline', 'Total'],
};

const bookmakerLinks = {
  'Prophet Exchange': 'https://prophetx.onelink.me/E5Yi/kxa7a4q7',
  'NoVig': 'https://novig.onelink.me/JHQQ/1vgeffy0',
  'DraftKings': 'https://dksb.sng.link/As9kz/85o9?_dl=https%3A%2F%2Fsportsbook.draftkings.com%2Fgateway%3Fs%3D356902394%26wpcid%3D239969%26wpcn%3DBet5Win150%26wpsrc%3DBetsperts%26wpcrid%3D%26wpscn%3D&pcid=239969&psn=Betsperts&pcn=Bet5Win150_4For4',
  'FanDuel':'https://wlfanduelus.adsrv.eacdn.com/C.ashx?btag=a_21545b_16c_&affid=11060&siteid=21545&adid=16&c=',
  'Bet365': 'https://www.bet365.com/olp/open-account?affiliate=365_02038136',
  'BetMGM': 'https://mediaserver.betmgmpartners.com/renderBanner.do?zoneId=1700764',
  'Caesars': 'https://wlwilliamhillus.adsrv.eacdn.com/C.ashx?btag=a_12260b_2091c_&affid=47&siteid=12260&adid=2091&c=',
  'Fanatics': 'https://go.affiliationcloud.com/0002JY',
};

// ══════════════════════════════════════════════════════════
//  STATIC STYLES — hoisted outside component to avoid per-render allocation
// ══════════════════════════════════════════════════════════

const customTheme = createTheme({
  palette: {
    primary: { main: '#ff1744', contrastText: '#ffffff' },
    background: { default: '#121212', paper: '#1e1e1e' },
  },
  typography: { fontFamily: 'Roboto, Arial, sans-serif', h5: { fontWeight: 600 } },
  components: {
    MuiInputLabel: { styleOverrides: { root: { color: '#ffffff' } } },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          color: '#ffffff',
          backgroundColor: '#333333',
          '& .MuiOutlinedInput-notchedOutline': { borderColor: '#ffffff' },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#ffffff' },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#ffffff' },
        },
      },
    },
    MuiMenu: { styleOverrides: { paper: { backgroundColor: '#333333', color: '#ffffff' } } },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          color: '#ffffff',
          '&.Mui-selected': { backgroundColor: '#444444' },
          '&.Mui-selected:hover': { backgroundColor: '#555555' },
          '&:hover': { backgroundColor: '#444444' },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: { root: { color: '#ffffff', '&.Mui-checked': { color: '#ff1744' } } },
    },
  },
});

const SX_COL_OUTER = { display: 'flex', flexDirection: 'column', gap: '0', justifyContent: 'center', height: '100%' };
const SX_EMPTY_CELL = { textAlign: 'right', pr: '12px' };
const STYLE_BEST_IMG = { width: '30px', height: '30px', objectFit: 'contain', flexShrink: 0 };
const STYLE_BEST_IMG_M = { width: '22px', height: '22px', objectFit: 'contain', flexShrink: 0 };
const FIXED_COLUMNS = ['eventName', 'eventStartFormatted', 'bestOdds'];

// Format limits for display (e.g. "$1.5k", "$998")
const formatLimitMax = (limits) => {
  const max = limits?.max;
  if (max == null) return null;
  return max >= 1000 ? `$${(max / 1000).toFixed(1).replace(/\.0$/, '')}k` : `$${Math.round(max)}`;
};

// ── Flash-fade: pure CSS animation replaces per-second JS timer ──
const FLASH_DURATION_SEC = 300;
const FLASH_KEYFRAMES_ID = 'flash-fade-keyframes';

// Inject keyframes once into document head (shared across all instances)
if (typeof document !== 'undefined' && !document.getElementById(FLASH_KEYFRAMES_ID)) {
  const style = document.createElement('style');
  style.id = FLASH_KEYFRAMES_ID;
  // Compositor-friendly flash: the tint lives on a pseudo-element and we animate
  // its OPACITY (GPU-composited), not background-color (main-thread style recalc
  // every frame for every flashing cell — this was saturating the main thread).
  style.textContent = `
    @keyframes flashFade { from { opacity: 1; } to { opacity: 0; } }
    .ss-flash { position: relative; isolation: isolate; }
    .ss-flash::before {
      content: ''; position: absolute; inset: 0; z-index: -1; pointer-events: none;
      background-color: rgb(13, 71, 161);
      will-change: opacity;
      animation: flashFade ${FLASH_DURATION_SEC}s linear forwards;
      animation-delay: var(--ss-flash-delay, 0s);
    }
  `;
  document.head.appendChild(style);
}

function getFlashStyle(updatedAt) {
  if (!updatedAt) return undefined;
  const ageSec = (Date.now() - new Date(updatedAt).getTime()) / 1000;
  if (ageSec < 0 || ageSec > FLASH_DURATION_SEC) return undefined;
  return { '--ss-flash-delay': `-${ageSec.toFixed(0)}s` };
}

// ── Shared Intl date/time formatters (reused across renders) ──
const DATE_FMT = new Intl.DateTimeFormat('en-US', { year: '2-digit', month: 'numeric', day: 'numeric' });
const TIME_FMT = new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });

const DATA_GRID_SX = {
  '& .MuiDataGrid-columnHeaders': {
    backgroundColor: '#1e1e1e', borderBottom: '2px solid #444',
    position: 'sticky', top: 0, zIndex: 2,
  },
  '& .MuiDataGrid-columnHeader': {
    userSelect: 'none', cursor: 'move', outline: 'none !important',
    '&:focus': { outline: 'none !important' },
    '&:focus-within': { outline: 'none !important' },
  },
  '& .MuiDataGrid-columnHeader--alignRight': { justifyContent: 'flex-end !important' },
  '& .MuiDataGrid-columnHeaderTitleContainer': {
    outline: 'none !important', '&:focus': { outline: 'none !important' },
  },
  '& .MuiDataGrid-cell': {
    color: '#ffffff !important', userSelect: 'none', padding: 0,
    borderRight: '1px solid rgba(255, 255, 255, 0.3)',
  },
  '& .MuiDataGrid-cell:last-child': { borderRight: 'none' },
  '& .MuiDataGrid-cell:focus': { outline: 'none' },
  '& .MuiDataGrid-cell:focus-within': { outline: 'none' },
  '& .MuiDataGrid-row:hover': { backgroundColor: '#1a1a1a' },
  // Pinned rows: subtle tint + accent so they read as pinned, not as a header
  '& .MuiDataGrid-pinnedRows': { backgroundColor: '#161616', boxShadow: 'none' },
  '& .MuiDataGrid-pinnedRows--top': { borderBottom: '2px solid #333' },
  '& .MuiDataGrid-pinnedRows .MuiDataGrid-row, & .MuiDataGrid-pinnedRows .MuiDataGrid-cell': { backgroundColor: '#161616' },
  // Pin button: hidden until row hover (or until the row is pinned)
  '& .pin-btn': { opacity: 0, transition: 'opacity 120ms' },
  '& .MuiDataGrid-row:hover .pin-btn': { opacity: 0.55 },
  '& .pin-btn:hover': { opacity: '1 !important' },
  '& .pin-btn.pinned': { opacity: 1 },
  '& .MuiDataGrid-row.Mui-selected': { backgroundColor: '#1a1a1a', '&:hover': { backgroundColor: '#1a1a1a' } },
  '& .MuiDataGrid-columnSeparator': { visibility: 'visible', color: 'rgba(255, 255, 255, 0.3)' },
  '& .MuiDataGrid-virtualScroller': { overflowY: 'auto !important' },
  '& .MuiDataGrid-root': { overflowY: 'auto !important', margin: 0 },
  '& .MuiDataGrid-main': { padding: 0 },
  '& .MuiDataGrid-pinnedColumns, & .MuiDataGrid-pinnedColumnHeaders': { backgroundColor: '#121212' },
  // Compact sticky footer (row count) — half the default 52px height
  '& .MuiDataGrid-footerContainer': { backgroundColor: '#1e1e1e', color: '#ffffff !important', position: 'sticky', bottom: 0, zIndex: 1, minHeight: '26px', height: '26px', borderTop: '1px solid #444' },
  '& .MuiDataGrid-rowCount': { fontSize: '0.75rem', margin: 0 },
  '& .MuiDataGrid-sortIcon': { color: '#ffffff !important' },
  '& .MuiTablePagination-root, & .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { color: '#ffffff !important' },
  '& .MuiSelect-icon': { color: '#ffffff !important' },
  '& .MuiDataGrid-menuIconButton': { color: '#ffffff !important' },
  '& .MuiDataGrid-detailPanelToggleCell': { color: '#ffffff !important' },
  '& .MuiDataGrid-columnHeaderTitle': {
    color: '#ffffff !important', whiteSpace: 'nowrap',
    overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 1.2,
  },
  '& .MuiDataGrid-columnHeaderDraggableContainer': { cursor: 'move' },
  // While a header is being dragged, the body ignores the pointer so :hover state
  // can't churn — otherwise every native `drag` event forces a full-grid style recalc.
  '&.ss-col-dragging .MuiDataGrid-virtualScrollerContent': { pointerEvents: 'none' },
  '&.ss-col-dragging .MuiDataGrid-row:hover': { backgroundColor: 'transparent' },
};

// ══════════════════════════════════════════════════════════
//  COMPONENT
// ══════════════════════════════════════════════════════════

const THROTTLE_MS = 500;


// ── Memoized bookmaker cell ──
// DataGridPro re-renders every visible cell on each column-drag hover step
// (and on every data flush). Memoizing the cell on its inputs makes those
// re-renders near-free when the underlying row objects haven't changed.
const _outcomeSxCache = new Map();
const outcomeBoxSx = (isBest, clickable, height) => {
  const k = `${isBest ? 1 : 0}${clickable ? 1 : 0}${height}`;
  let sx = _outcomeSxCache.get(k);
  if (!sx) {
    sx = {
      height: `${height}px`, display: 'flex', flexDirection: 'column', justifyContent: 'center', width: '100%',
      cursor: clickable ? 'pointer' : 'default',
      border: isBest ? '2px solid #00cc00' : 'none',
      '&:hover': clickable ? { backgroundColor: 'rgba(255,255,255,0.05)' } : {},
    };
    _outcomeSxCache.set(k, sx);
  }
  return sx;
};
const SX_ODDS_TXT_M = { fontSize: '12px', textAlign: 'right', pr: '4px', lineHeight: 1.2 };
const SX_ODDS_TXT_D = { fontSize: '14px', textAlign: 'right', pr: '12px', lineHeight: 1.2 };
const SX_VOL_TXT_M = { color: '#888888', fontSize: '0.65rem', textAlign: 'right', pr: '4px', lineHeight: 1 };
const SX_VOL_TXT_D = { color: '#888888', fontSize: '0.65rem', textAlign: 'right', pr: '12px', lineHeight: 1 };
const SX_EMPTY_TXT = { fontSize: '14px' };

const OutcomeBox = ({ value, limits, updatedAt, isBest, meta, field, showVolume, isMobile, height, onOpen }) => {
  const v = showVolume ? formatLimitMax(limits) : null;
  const flash = getFlashStyle(updatedAt);
  return (
    <Box sx={outcomeBoxSx(isBest, !!value, height)} style={flash} className={flash ? 'ss-flash' : undefined}
      onClick={() => meta && onOpen(meta, field)}>
      <Typography sx={isMobile ? SX_ODDS_TXT_M : SX_ODDS_TXT_D}>{value || ''}</Typography>
      {v ? <Typography sx={isMobile ? SX_VOL_TXT_M : SX_VOL_TXT_D}>{v}</Typography> : null}
    </Box>
  );
};

const BookCell = React.memo(function BookCell({ bookOdds, best, field, market, outcomeKeys, showVolume, isMobile, onOpen }) {
  if (!bookOdds) return <Box sx={SX_EMPTY_CELL}><Typography sx={SX_EMPTY_TXT} /></Box>;
  if (market === 'Summary') {
    return (
      <Box sx={SX_COL_OUTER}>
        <OutcomeBox value={bookOdds.topRow} limits={bookOdds.topRowLimits} updatedAt={bookOdds.topRowUpdatedDate}
          isBest={field === best?.topRow} meta={bookOdds.topRowMeta} field={field}
          showVolume={showVolume} isMobile={isMobile} height={35} onOpen={onOpen} />
        <OutcomeBox value={bookOdds.bottomRow} limits={bookOdds.bottomRowLimits} updatedAt={bookOdds.bottomRowUpdatedDate}
          isBest={field === best?.bottomRow} meta={bookOdds.bottomRowMeta} field={field}
          showVolume={showVolume} isMobile={isMobile} height={35} onOpen={onOpen} />
      </Box>
    );
  }
  const keys = outcomeKeys || (market === 'Moneyline' || market === 'Spread' ? ['Away', 'Home'] : ['Over', 'Under']);
  return (
    <Box sx={SX_COL_OUTER}>
      {keys.map(outcome => (
        <OutcomeBox key={outcome} value={bookOdds[outcome]} limits={bookOdds.limits?.[outcome]}
          updatedAt={bookOdds.updatedDates?.[outcome]} isBest={field === best?.[outcome]}
          meta={bookOdds.meta?.[outcome]} field={field}
          showVolume={showVolume} isMobile={isMobile} height={35} onOpen={onOpen} />
      ))}
    </Box>
  );
});

// Selected books live under ONE key. They used to be split by breakpoint, but
// useMediaQuery reports false on the first render, so on phones the page read
// the desktop list and then the persist effect wrote it over the mobile one.
const BOOKS_KEY = 'oddsSelectedBooks';
const LEGACY_BOOKS_KEYS = ['oddsSelectedBooks_desktop', 'oddsSelectedBooks_mobile'];

// Returns the saved array, or null if nothing valid is stored.
const readSavedBooks = () => {
  for (const key of [BOOKS_KEY, ...LEGACY_BOOKS_KEYS]) {
    try {
      const parsed = JSON.parse(localStorage.getItem(key));
      if (Array.isArray(parsed)) return parsed;
    } catch { /* missing/corrupt — try the next key */ }
  }
  return null;
};

const Odds = ({ isAuthenticated = false }) => {
  const sportsbooks = useStoreData('sportsbooks');
  const leaguesConfig = useStoreData('leagues') || [];
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  // Mobile-only: dropdowns/checkboxes collapse behind the filter toggle; search stays visible
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const apiRef = useGridApiRef();

  // ── Logo lookup maps (resolved from reference tables, not per-record) ──
  const { bookLogos, teamMap } = useLogoMaps();

  // ── Available books: always derived from server-provided sportsbooks config (ingest=1) ──
  const availableBooks = useMemo(() => {
    return sportsbooks.filter(sb => !sb.pickem).map(sb => sb.name).filter(Boolean).sort((a, b) => a.localeCompare(b));
  }, [sportsbooks]);

  // ── Filter state (persisted to localStorage) ──
  const [sport, setSport] = useState(() => localStorage.getItem('oddsSport') || 'NFL');
  const [searchText, setSearchText] = useState('');
  const [market, setMarket] = useState(() => localStorage.getItem('oddsMarket') || 'Summary');
  // Pinned rows (event keys), persisted per sport
  const [pinnedIds, setPinnedIds] = useState([]);
  const marketRef = useRef(market);
  marketRef.current = market;
  const [periodType, setPeriodType] = useState(() => localStorage.getItem('oddsPeriodType') || 'Full Game');

  // ── Odds subscription (ref-based, NOT useStoreData) ──
  // useStoreData re-renders this component on EVERY odds message and copies the
  // whole odds Map (Array.from) inside render. During rapid update periods that
  // is dozens of full-component renders per second, which starves the native
  // column-drag. Here we subscribe directly, keep the store off the render path,
  // and materialize the array at most once per THROTTLE_MS.
  const readBets = useCallback(() => {
    const data = dataStore.get('odds');
    const arr = data instanceof Map ? Array.from(data.values()) : (data || []);
    return arr.filter(b => b && !b.pickem);
  }, []);
  const [throttledBets, setThrottledBets] = useState(readBets);
  const throttleRef = useRef(null);
  const dirtyRef = useRef(false);
  // latestBetsRef is a lazy accessor so we never materialize the array unless we flush
  const latestBetsRef = useRef(null);
  latestBetsRef.current = readBets;

  // Column-drag guard: while a header is being dragged, data updates are held
  // so the grid isn't re-rendered mid-drag (which drops the drag / freezes).
  const dragActiveRef = useRef(false);
  const dragPendingRef = useRef(false);

  useEffect(() => {
    const flush = () => {
      dirtyRef.current = false;
      setThrottledBets(readBets());
    };
    const scheduleTrailing = () => {
      throttleRef.current = setTimeout(() => {
        throttleRef.current = null;
        if (!dirtyRef.current) return;
        if (dragActiveRef.current) { dragPendingRef.current = true; return; }
        flush();
        scheduleTrailing();
      }, THROTTLE_MS);
    };
    const unsubscribe = dataStore.subscribe('odds', () => {
      dirtyRef.current = true;
      if (dragActiveRef.current) { dragPendingRef.current = true; return; }
      if (throttleRef.current) return; // trailing edge will pick it up
      flush();                           // leading edge
      scheduleTrailing();
    });
    return () => {
      unsubscribe();
      if (throttleRef.current) { clearTimeout(throttleRef.current); throttleRef.current = null; }
    };
  }, [readBets]);

  // (Flash-fade handled by pure CSS animation — no JS timer needed)

  // ── Selected books (persisted user intent — survives books dropping out of data) ──
  // null = "not initialized yet, default to all when data arrives"
  const [selectedBooks, setSelectedBooks] = useState(readSavedBooks);

  // ── Odds history modal ──
  const { historyOpen, historyData, historyLoading, historyMeta, openOddsHistory, closeOddsHistory } = useOddsHistory();

  // ── Markets modal: click a team name to see every market for that game ──
  const { marketsOpen, marketsData, marketsMeta, openMarkets, closeMarkets } = useMarkets();

  const handleTeamClick = useCallback((row, clickedTeam) => {
    if (!row?.fixtureId) return;
    // Snapshot sides are Away/Home; the alt grid uses team names — pass both.
    const teamSide = clickedTeam
      ? (clickedTeam === row.homeTeam ? 'Home' : 'Away')
      : null;
    const markets = buildMarkets(throttledBets, {
      fixtureId: row.fixtureId, bookLogos, teamName: clickedTeam || null, teamSide,
    });
    openMarkets({
      title: clickedTeam || `${row.awayTeam} @ ${row.homeTeam}`,
      teamName: clickedTeam || '',
      fixture_id: row.fixtureId,
      bookLogos,
      league: sport,
      awayTeam: row.awayAbbr || row.awayTeam,
      homeTeam: row.homeAbbr || row.homeTeam,
      awayTeamLogo: row.awayTeamLogo,
      homeTeamLogo: row.homeTeamLogo,
      eventStart: row.eventStart,
    }, markets);
  }, [openMarkets, sport, throttledBets, bookLogos]);

  // Default to all books on first data load (only if user has no saved selection)
  const initializedRef = useRef(false);
  useEffect(() => {
    if (initializedRef.current || availableBooks.length === 0) return;
    initializedRef.current = true;
    if (selectedBooks === null) {
      setSelectedBooks([...availableBooks]);
    }
  }, [availableBooks]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selectedBooks === null) return;
    try {
      localStorage.setItem(BOOKS_KEY, JSON.stringify(selectedBooks));
      LEGACY_BOOKS_KEYS.forEach(k => localStorage.removeItem(k)); // one-time cleanup of the split-key era
    } catch { /* storage unavailable */ }
  }, [selectedBooks]);

  useEffect(() => { localStorage.setItem('oddsSport', sport); }, [sport]);

  // ── Pinned rows: load on sport change, save on edit ──
  useEffect(() => {
    try { setPinnedIds(JSON.parse(localStorage.getItem(`oddsPinnedRows_${sport}`) || '[]')); }
    catch (e) { setPinnedIds([]); }
  }, [sport]);

  const togglePin = useCallback((id) => {
    setPinnedIds((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      try { localStorage.setItem(`oddsPinnedRows_${sport}`, JSON.stringify(next)); } catch (e) { /* ignore */ }
      return next;
    });
  }, [sport]);
  useEffect(() => { localStorage.setItem('oddsMarket', market); }, [market]);
  useEffect(() => { localStorage.setItem('oddsPeriodType', periodType); }, [periodType]);

  // ── Volume toggle (persisted to localStorage) ──
  // -- Show live toggle (off by default; requires subscription) --
  const [showLive, setShowLive] = useState(() => localStorage.getItem('oddsShowLive') === 'true');
  useEffect(() => { localStorage.setItem('oddsShowLive', JSON.stringify(showLive)); }, [showLive]);
  // Effective value: persisted preference only honored for subscribers
  const liveVisible = isAuthenticated && showLive;

  // ── Odds format (global; toggled from the sidebar) ──
  const oddsFormat = useOddsFormat();

  const [showVolume, setShowVolume] = useState(() => localStorage.getItem('oddsShowVolume') === 'true');
  const showVolumeRef = useRef(showVolume);
  showVolumeRef.current = showVolume;
  useEffect(() => { localStorage.setItem('oddsShowVolume', JSON.stringify(showVolume)); }, [showVolume]);

  // ── League options: from server-provided config (ingest=1 from DB) ──
  const leagueOptions = useMemo(() => {
    if (leaguesConfig.length > 0) {
      return leaguesConfig.map(l => l.name || l.id).filter(Boolean).sort();
    }
    // Fallback to data-derived leagues if config not loaded yet
    const fromData = [...new Set((throttledBets || []).map(b => b.league).filter(Boolean))];
    return fromData.length > 0 ? fromData.sort() : ['NBA'];
  }, [leaguesConfig, throttledBets]);

  // Reset sport if saved value no longer exists in available leagues.
  // Guard: only validate once the server-provided leagues config has loaded —
  // otherwise the ['NBA'] fallback fires on mount and clobbers the saved
  // sport (which cascades into market/periodType resets) before real data arrives.
  useEffect(() => {
    if (leaguesConfig.length === 0) return;
    if (leagueOptions.length > 0 && !leagueOptions.includes(sport)) {
      setSport(leagueOptions[0]);
    }
  }, [leaguesConfig, leagueOptions, sport]);

  // Resolve sport_id for the selected league (e.g. 'soccer', 'basketball')
  // Used for dynamic market mapping — soccer leagues all share the same mappings
  const currentSportId = useMemo(() => {
    const league = leaguesConfig.find(l => (l.name || l.id) === sport);
    return league?.sport_id || null;
  }, [leaguesConfig, sport]);

  // Market filter: sport-specific mapping with dynamic soccer support
  const getMarketFilter = useCallback((marketLabel) => {
    if (currentSportId === 'soccer') {
      const soccerMap = { Spread: 'asian_handicap', Total: 'total_goals', Moneyline: 'moneyline' };
      return soccerMap[marketLabel] || marketFilterMap[marketLabel];
    }
    return (marketFilterBySport[sport] && marketFilterBySport[sport][marketLabel]) || marketFilterMap[marketLabel];
  }, [currentSportId, sport]);

  const periodTypes = useMemo(() => {
    // Use currentSportId for sports where league names don't match config keys (soccer)
    let key = sport;
    if (currentSportId === 'soccer') key = '__soccer__';
    const all = periodTypesBySport[key] || periodTypesBySport[sport] || ['Full Game'];
    const restricted = noFullGameMarkets[key] || noFullGameMarkets[sport];
    if (restricted && restricted.has(market)) {
      return all.filter(p => p !== 'Full Game');
    }
    return all;
  }, [sport, market, currentSportId]);
  const marketOptions = useMemo(() => marketsBySport[sport] || defaultMarkets, [sport]);

  // Same guard as the sport reset: soccer leagues resolve their period list via
  // currentSportId (leagues config). Before it loads they fall back to
  // ['Full Game'], which would reset and persist over a saved 'First Half'.
  useEffect(() => {
    if (leaguesConfig.length === 0) return;
    if (!periodTypes.includes(periodType)) setPeriodType(periodTypes[0] || 'Full Game');
  }, [leaguesConfig.length, periodTypes, periodType]);

  useEffect(() => {
    if (!marketOptions.includes(market)) setMarket(marketOptions[0] || 'Moneyline');
  }, [marketOptions, market]);

  const handleBooksFilterChange = useCallback((event) => {
    const value = event.target.value;
    const clicked = value[value.length - 1];
    if (clicked === 'all') {
      setSelectedBooks(prev => (prev || []).length === availableBooks.length ? [] : [...availableBooks]);
    } else if (isGroupValue(clicked)) {
      // Combo preset: select exactly the books in this group (that exist here)
      const group = groupFromValue(clicked);
      if (group) setSelectedBooks(booksInGroup(group, availableBooks));
    } else {
      setSelectedBooks(value);
    }
  }, [availableBooks]);

  const renderBooksValue = useCallback((selected) => {
    if (!selected || !selected.length) return 'None';
    const visibleCount = selected.filter(b => availableBooks.includes(b)).length;
    if (visibleCount === availableBooks.length) return 'All';
    if (visibleCount > 1) return `Multiple (${visibleCount})`;
    return selected.filter(b => availableBooks.includes(b))[0] || 'None';
  }, [availableBooks]);

  // ══════════════════════════════════════════════════════════
  //  TABLE DATA — group throttled bets into rows
  // ══════════════════════════════════════════════════════════

  const rows = useMemo(() => {
    if (!Array.isArray(throttledBets)) return [];

    const filteredBets = throttledBets.filter(bet =>
      bet && bet.league === sport && bet.marketCategory === 'Game Line' && (liveVisible || !bet.isLive)
    );

    const eventGroups = {};
    filteredBets.forEach(bet => {
      const key = bet.fixture_id || bet.eventName;
      if (!eventGroups[key]) {
        eventGroups[key] = { event: bet.eventName, startTime: bet.eventStart, bets: [] };
      }
      eventGroups[key].bets.push(bet);
    });

    const result = [];
    const booksToRender = selectedBooks || [];

    const isSoccerML = currentSportId === 'soccer' && market === 'Moneyline';

    Object.entries(eventGroups).forEach(([eventKey, group]) => {
      // Every bet for this fixture was filtered out (e.g. Show Live off on a
      // live game). Without this the row still renders, just empty.
      if (!group.bets.length) return;

      const startDate = group.startTime ? new Date(group.startTime) : null;

      const row = {
        id: eventKey,
        fixtureId: group.bets[0]?.fixture_id || '',
        eventName: group.event,
        isLive: group.bets.some(b => b.isLive),
        awayTeam: '', homeTeam: '',
        awayTeamLogo: '', homeTeamLogo: '',
        isThreeWay: isSoccerML,
        eventStart: startDate || new Date(),
        eventStartFormatted: {
          date: startDate ? DATE_FMT.format(startDate) : '',
          time: startDate ? TIME_FMT.format(startDate) : '',
        },
        bookmakerOdds: {},
        bestOddsBookmakers: { Away: '', Home: '', Draw: '', Over: '', Under: '', topRow: '', bottomRow: '' },
      };

      const isPlayerSport = sport === 'ATP' || sport === 'WTA' || sport === 'PGA';

      // Reference bets for team/player names
      const referenceBets = group.bets.filter(bet =>
        isPlayerSport ? bet.market === 'moneyline' : (bet.market === 'moneyline' || SPREAD_MARKETS.has(bet.market))
      );

      if (referenceBets.length > 0) {
        const reliableSources = ['DraftKings', 'FanDuel', 'Pinnacle'];
        const awayBet = referenceBets.find(b => b.side === 'Away' && reliableSources.includes(b.bookName))
                      || referenceBets.find(b => b.side === 'Away');
        let homeBet = referenceBets.find(b => b.side === 'Home' && reliableSources.includes(b.bookName))
                    || referenceBets.find(b => b.side === 'Home');

        if (isPlayerSport && awayBet && homeBet && awayBet.playerId === homeBet.playerId) {
          homeBet = referenceBets.find(b => b.side === 'Home' && b.playerId !== awayBet.playerId)
                  || referenceBets.find(b => b.playerId !== awayBet.playerId) || homeBet;
        }
        if (isPlayerSport) {
          row.awayTeam = awayBet ? (awayBet.playerName || awayBet.selection || 'Unknown Player') : 'Unknown Player';
          row.homeTeam = homeBet ? (homeBet.playerName || homeBet.selection || 'vs Unknown') : 'vs Unknown';
        } else {
          // One-sided straggler odds (a purged fixture that left only an Away
          // or only a Home rung) used to render "Unknown Team A/B". The event
          // name always carries both teams, so parse it for whichever side has
          // no reference bet rather than showing a placeholder.
          const sepN = group.event && group.event.includes(' vs ') ? ' vs ' : ' @ ';
          const [nameA, nameB] = (group.event || '').split(sepN);
          const fallbackAway = nameA ? nameA.trim().replace(/ -$/, '') : 'Unknown Team A';
          const fallbackHome = nameB ? nameB.trim().replace(/ -$/, '') : 'Unknown Team B';

          row.awayTeam = awayBet ? (teamMap[awayBet.awayTeamId]?.name || awayBet.selection || fallbackAway) : fallbackAway;
          row.homeTeam = homeBet ? (teamMap[homeBet.homeTeamId]?.name || homeBet.selection || fallbackHome) : fallbackHome;
          row.awayTeamLogo = awayBet ? (teamMap[awayBet.awayTeamId]?.logo || '') : '';
          row.homeTeamLogo = homeBet ? (teamMap[homeBet.homeTeamId]?.logo || '') : '';
          row.awayAbbr = awayBet ? (teamMap[awayBet.awayTeamId]?.abbreviation || '') : '';
          row.homeAbbr = homeBet ? (teamMap[homeBet.homeTeamId]?.abbreviation || '') : '';
        }
      } else {
        if (isPlayerSport) {
          const parts = group.event.includes(' vs ') ? group.event.split(' vs ') : group.event.split(' @ ');
          row.awayTeam = parts[0] ? parts[0].trim() : 'Unknown Player';
          row.homeTeam = parts[1] ? parts[1].trim() : 'vs Unknown';
        } else {
          const separator = group.event.includes(' vs ') ? ' vs ' : ' @ ';
          const [tA, tB] = group.event.split(separator);
          row.awayTeam = tA ? tA.trim().replace(/ -$/, '') : 'Unknown Team A';
          row.homeTeam = tB ? tB.trim().replace(/ -$/, '') : 'Unknown Team B';
          row.awayTeamLogo = teamMap[group.bets[0]?.awayTeamId]?.logo || '';
          row.homeTeamLogo = teamMap[group.bets[0]?.homeTeamId]?.logo || '';
          row.awayAbbr = teamMap[group.bets[0]?.awayTeamId]?.abbreviation || '';
          row.homeAbbr = teamMap[group.bets[0]?.homeTeamId]?.abbreviation || '';
        }
      }

      // ── SUMMARY VIEW ──
      if (market === 'Summary') {
        const spreadBets = group.bets.filter(b => SPREAD_MARKETS.has(b.market) && b.periodType === periodType);
        const totalBets = group.bets.filter(b => TOTAL_MARKETS.has(b.market) && b.periodType === periodType);

        const spreadOutcomes = { Away: {}, Home: {} };
        const spreadDetails  = { Away: {}, Home: {} };
        const spreadUpdated  = { Away: {}, Home: {} };

        spreadBets.forEach(bet => {
          const s = bet.side;
          spreadOutcomes[s][bet.bookName] = bet.price;
          spreadDetails[s][`${bet.bookName}Points`] = bet.points;
          spreadDetails[s][`${bet.bookName}Logo`] = bookLogos[bet.bookName] || '';
          spreadDetails[s][`${bet.bookName}LineDisplay`] = `${bet.points > 0 ? '+' : ''}${bet.points}`;
          spreadUpdated[s][bet.bookName] = bet.updatedAt;
          spreadDetails[s][`${bet.bookName}Limits`] = bet.limits;
          spreadDetails[s][`${bet.bookName}Meta`] = {
            fixture_id: bet.fixture_id,
            market_id: bet.market_id,
            selection: bet.selection,
            selection_line: bet.selection_line,
            player_id: bet.playerId || '',
            points: bet.points,
            playerName: bet.playerName || `${row.awayAbbr || row.awayTeam} @ ${row.homeAbbr || row.homeTeam}`,
            marketDisplay: bet.marketName || 'Spread',
            league: sport,
            awayTeam: row.awayAbbr || row.awayTeam,
            homeTeam: row.homeAbbr || row.homeTeam,
            awayTeamLogo: row.awayTeamLogo,
            homeTeamLogo: row.homeTeamLogo,
            eventStart: row.eventStart,
            bookLogo: bookLogos[bet.bookName] || '',
          };
        });

        let favoriteId = 'Away';
        for (const book of booksToRender) {
          const awayPts = spreadDetails.Away[`${book}Points`];
          const homePts = spreadDetails.Home[`${book}Points`];
          if (awayPts !== undefined && homePts !== undefined) {
            favoriteId = awayPts < 0 ? 'Away' : 'Home';
            break;
          }
        }

        const totalOutcomes = { Over: {} };
        const totalDetails  = { Over: {} };
        const totalUpdated  = { Over: {} };

        totalBets.forEach(bet => {
          if (bet.side === 'Over') {
            totalOutcomes.Over[bet.bookName] = bet.price;
            totalDetails.Over[`${bet.bookName}Points`] = bet.points;
            totalDetails.Over[`${bet.bookName}Logo`] = bookLogos[bet.bookName] || '';
            totalDetails.Over[`${bet.bookName}LineDisplay`] = `o${bet.points || ''}`;
            totalUpdated.Over[bet.bookName] = bet.updatedAt;
            totalDetails.Over[`${bet.bookName}Limits`] = bet.limits;
            totalDetails.Over[`${bet.bookName}Meta`] = {
              fixture_id: bet.fixture_id,
              market_id: bet.market_id,
              selection: bet.selection,
              selection_line: bet.selection_line,
              player_id: bet.playerId || '',
              points: bet.points,
              playerName: bet.playerName || `${row.awayAbbr || row.awayTeam} @ ${row.homeAbbr || row.homeTeam}`,
              marketDisplay: bet.marketName || 'Total',
              league: sport,
              awayTeam: row.awayAbbr || row.awayTeam,
              homeTeam: row.homeAbbr || row.homeTeam,
              awayTeamLogo: row.awayTeamLogo,
              homeTeamLogo: row.homeTeamLogo,
              eventStart: row.eventStart,
              bookLogo: bookLogos[bet.bookName] || '',
            };
          }
        });

        const bestOdds = {};
        const favoriteSpreadEntries = [];
        booksToRender.forEach(book => {
          const price = spreadOutcomes[favoriteId][book] ? parseFloat(spreadOutcomes[favoriteId][book]) : null;
          const points = spreadDetails[favoriteId][`${book}Points`];
          if (price !== null) {
            favoriteSpreadEntries.push({
              book, price,
              points: points !== undefined ? parseFloat(points) : null,
              lineDisplay: spreadDetails[favoriteId][`${book}LineDisplay`],
              logo: spreadDetails[favoriteId][`${book}Logo`],
            });
          }
        });

        const isSpreadTopRow = favoriteId === 'Away';
        const spreadRowKey = isSpreadTopRow ? 'topRow' : 'bottomRow';
        const totalRowKey = isSpreadTopRow ? 'bottomRow' : 'topRow';

        if (favoriteSpreadEntries.length > 0) {
          favoriteSpreadEntries.sort((a, b) => b.price - a.price);
          const best = favoriteSpreadEntries[0];
          bestOdds[spreadRowKey] = {
            price: best.price, lineDisplay: best.lineDisplay, logo: best.logo,
            display: `${best.lineDisplay} ${fmtPrice(best.price, oddsFormat)}`, type: 'spread',
            meta: spreadDetails[favoriteId][`${best.book}Meta`] || null,
          };
          row.bestOddsBookmakers[spreadRowKey] = best.book;
        } else {
          bestOdds[spreadRowKey] = { display: '', logo: '', type: 'spread' };
        }

        const overEntries = [];
        booksToRender.forEach(book => {
          const price = totalOutcomes.Over[book] ? parseFloat(totalOutcomes.Over[book]) : null;
          const points = totalDetails.Over[`${book}Points`];
          if (price !== null) {
            overEntries.push({
              book, price,
              points: points !== undefined ? parseFloat(points) : null,
              lineDisplay: totalDetails.Over[`${book}LineDisplay`],
              logo: totalDetails.Over[`${book}Logo`],
            });
          }
        });

        if (overEntries.length > 0) {
          overEntries.sort((a, b) => a.points !== b.points ? a.points - b.points : b.price - a.price);
          const best = overEntries[0];
          bestOdds[totalRowKey] = {
            price: best.price, lineDisplay: best.lineDisplay, logo: best.logo,
            display: `${best.lineDisplay} ${fmtPrice(best.price, oddsFormat)}`, type: 'total',
            meta: totalDetails.Over[`${best.book}Meta`] || null,
          };
          row.bestOddsBookmakers[totalRowKey] = best.book;
        } else {
          bestOdds[totalRowKey] = { display: '', logo: '', type: 'total' };
        }

        row.summaryMetadata = { favoriteId, isSpreadTopRow };

        booksToRender.forEach(book => {
          const odds = { topRow: '', bottomRow: '', topRowUpdatedDate: null, bottomRowUpdatedDate: null, topRowMeta: null, bottomRowMeta: null };

          if (isSpreadTopRow) {
            const sp = spreadOutcomes[favoriteId][book] ? parseFloat(spreadOutcomes[favoriteId][book]) : null;
            if (sp !== null) {
              const line = spreadDetails[favoriteId][`${book}LineDisplay`] || '';
              odds.topRow = `${line} ${fmtPrice(sp, oddsFormat)}`;
              odds.topRowUpdatedDate = spreadUpdated[favoriteId][book];
              odds.topRowMeta = spreadDetails[favoriteId][`${book}Meta`] || null;
              odds.topRowLimits = spreadDetails[favoriteId][`${book}Limits`] || null;
            }
            const op = totalOutcomes.Over[book] ? parseFloat(totalOutcomes.Over[book]) : null;
            if (op !== null) {
              const line = totalDetails.Over[`${book}LineDisplay`] || '';
              odds.bottomRow = `${line} ${fmtPrice(op, oddsFormat)}`;
              odds.bottomRowUpdatedDate = totalUpdated.Over[book];
              odds.bottomRowMeta = totalDetails.Over[`${book}Meta`] || null;
              odds.bottomRowLimits = totalDetails.Over[`${book}Limits`] || null;
            }
          } else {
            const op = totalOutcomes.Over[book] ? parseFloat(totalOutcomes.Over[book]) : null;
            if (op !== null) {
              const line = totalDetails.Over[`${book}LineDisplay`] || '';
              odds.topRow = `${line} ${fmtPrice(op, oddsFormat)}`;
              odds.topRowUpdatedDate = totalUpdated.Over[book];
              odds.topRowMeta = totalDetails.Over[`${book}Meta`] || null;
              odds.topRowLimits = totalDetails.Over[`${book}Limits`] || null;
            }
            const sp = spreadOutcomes[favoriteId][book] ? parseFloat(spreadOutcomes[favoriteId][book]) : null;
            if (sp !== null) {
              const line = spreadDetails[favoriteId][`${book}LineDisplay`] || '';
              odds.bottomRow = `${line} ${fmtPrice(sp, oddsFormat)}`;
              odds.bottomRowUpdatedDate = spreadUpdated[favoriteId][book];
              odds.bottomRowMeta = spreadDetails[favoriteId][`${book}Meta`] || null;
              odds.bottomRowLimits = spreadDetails[favoriteId][`${book}Limits`] || null;
            }
          }

          row.bookmakerOdds[book] = odds;
          row[book] = '';
        });

        row.bestOdds = bestOdds;

      } else {
        // ── SINGLE MARKET VIEW ──
        const targetMarket = getMarketFilter(market);
        const oddsBets = group.bets.filter(b => b.market === targetMarket && b.periodType === periodType);

        const isMLOrSpread = market === 'Moneyline' || market === 'Spread';
        const outcomeKeys = isSoccerML ? ['Away', 'Draw', 'Home'] : (isMLOrSpread ? ['Away', 'Home'] : ['Over', 'Under']);
        row.outcomeKeys = outcomeKeys;

        const outcomes = {};
        const outcomeDetails = {};
        const outcomeUpdatedDates = {};
        outcomeKeys.forEach(k => { outcomes[k] = {}; outcomeDetails[k] = {}; outcomeUpdatedDates[k] = {}; });

        oddsBets.forEach(bet => {
          const outcome = bet.side;
          if (!outcomes[outcome]) return;

          outcomes[outcome][bet.bookName] = bet.price;
          outcomeDetails[outcome][`${bet.bookName}Points`] = bet.points;
          outcomeDetails[outcome][`${bet.bookName}Logo`] = bookLogos[bet.bookName] || '';
          outcomeDetails[outcome][`${bet.bookName}OutcomeName`] = outcome;
          outcomeUpdatedDates[outcome][bet.bookName] = bet.updatedAt;
          outcomeDetails[outcome][`${bet.bookName}Limits`] = bet.limits;
          outcomeDetails[outcome][`${bet.bookName}Meta`] = {
            fixture_id: bet.fixture_id,
            market_id: bet.market_id,
            selection: bet.selection,
            selection_line: bet.selection_line,
            player_id: bet.playerId || '',
            points: bet.points,
            playerName: bet.playerName || (market === 'Moneyline' ? (bet.selection || '') : `${row.awayAbbr || row.awayTeam} @ ${row.homeAbbr || row.homeTeam}`),
            marketDisplay: bet.marketName || market || '',
            league: sport,
            awayTeam: row.awayAbbr || row.awayTeam,
            homeTeam: row.homeAbbr || row.homeTeam,
            awayTeamLogo: row.awayTeamLogo,
            homeTeamLogo: row.homeTeamLogo,
            eventStart: row.eventStart,
            bookLogo: bookLogos[bet.bookName] || '',
          };

          if (market === 'Spread') {
            outcomeDetails[outcome][`${bet.bookName}LineDisplay`] = `${bet.points > 0 ? '+' : ''}${bet.points}`;
          } else if (market === 'Total') {
            outcomeDetails[outcome][`${bet.bookName}LineDisplay`] = `${outcome === 'Over' ? 'o' : 'u'}${bet.points || ''}`;
          } else {
            outcomeDetails[outcome][`${bet.bookName}LineDisplay`] = '';
          }
        });

        const bestOdds = {};
        outcomeKeys.forEach(outcome => {
          const entries = [];
          booksToRender.forEach(book => {
            const price = outcomes[outcome][book] ? parseFloat(outcomes[outcome][book]) : null;
            const points = outcomeDetails[outcome][`${book}Points`];
            if (price !== null) {
              entries.push({
                book, price,
                points: points !== undefined ? parseFloat(points) : null,
                logo: outcomeDetails[outcome][`${book}Logo`],
                outcomeName: outcomeDetails[outcome][`${book}OutcomeName`],
                lineDisplay: outcomeDetails[outcome][`${book}LineDisplay`],
              });
            }
          });

          let bestEntry = null;
          if (entries.length > 0) {
            if (market === 'Total') {
              entries.sort((a, b) => {
                if (a.points !== b.points) return outcome === 'Over' ? a.points - b.points : b.points - a.points;
                return b.price - a.price;
              });
            } else {
              entries.sort((a, b) => b.price - a.price);
            }
            bestEntry = entries[0];
          }

          if (bestEntry) {
            bestOdds[outcome] = {
              price: bestEntry.price, points: bestEntry.points, logo: bestEntry.logo,
              outcomeName: bestEntry.outcomeName, lineDisplay: bestEntry.lineDisplay,
              meta: outcomeDetails[outcome][`${bestEntry.book}Meta`] || null,
            };
            row.bestOddsBookmakers[outcome] = bestEntry.book;
          } else {
            bestOdds[outcome] = { price: null, points: null, logo: '', outcomeName: outcome, lineDisplay: '' };
            row.bestOddsBookmakers[outcome] = '';
          }
        });

        booksToRender.forEach(book => {
          const odds = { updatedDates: {}, meta: {}, limits: {} };
          outcomeKeys.forEach(outcome => {
            const price = outcomes[outcome][book] ? parseFloat(outcomes[outcome][book]) : null;
            if (price !== null) {
              const fmt = fmtPrice(price, oddsFormat);
              const line = outcomeDetails[outcome][`${book}LineDisplay`] || '';
              odds[outcome] = line ? `${line} ${fmt}` : fmt;
              odds.updatedDates[outcome] = outcomeUpdatedDates[outcome][book];
              odds.meta[outcome] = outcomeDetails[outcome][`${book}Meta`] || null;
              odds.limits[outcome] = outcomeDetails[outcome][`${book}Limits`] || null;
            } else {
              odds[outcome] = '';
              odds.updatedDates[outcome] = null;
              odds.meta[outcome] = null;
              odds.limits[outcome] = null;
            }
          });
          row.bookmakerOdds[book] = odds;
          row[book] = '';
        });

        outcomeKeys.forEach(outcome => {
          const { price, lineDisplay } = bestOdds[outcome] || {};
          if (price !== null && price !== undefined) {
            const fmt = fmtPrice(price, oddsFormat);
            bestOdds[outcome].display = lineDisplay ? `${lineDisplay} ${fmt}` : fmt;
          } else {
            bestOdds[outcome].display = '';
          }
        });

        row.bestOdds = bestOdds;
      }

      result.push(row);
    });

    result.sort((a, b) => a.eventStart - b.eventStart);
    // Search filter: team names/abbrs, event
    const q = searchText.trim().toLowerCase();
    const searched = !q ? result : result.filter(r => {
      const hay = `${r.eventName || ''} ${r.awayTeam || ''} ${r.homeTeam || ''} ${r.awayAbbr || ''} ${r.homeAbbr || ''}`.toLowerCase();
      return q.split(/\s+/).every(term => hay.includes(term));
    });
    return searched;

  }, [throttledBets, sport, market, periodType, selectedBooks, bookLogos, teamMap, getMarketFilter, currentSportId, liveVisible, oddsFormat, searchText]);

  // ══════════════════════════════════════════════════════════
  //  COLUMN ORDER PERSISTENCE — keyed by sport + device only
  // ══════════════════════════════════════════════════════════

  const getSavedColumnOrder = useCallback(() => {
    try {
      const saved = localStorage.getItem('oddsColumnOrder');
      if (saved) return JSON.parse(saved);
    } catch (e) { /* ignore */ }
    return null;
  }, []);

  const saveColumnOrder = useCallback((order) => {
    try {
      localStorage.setItem('oddsColumnOrder', JSON.stringify(order));
    } catch (e) { /* ignore */ }
  }, []);

  // ══════════════════════════════════════════════════════════
  //  COLUMNS — does NOT depend on `market` (renderCell reads it via ref)
  // ══════════════════════════════════════════════════════════

  // Pinned rows render in DataGridPro's top pinned section; keep them out of the main body.
  const pinnedSet = useMemo(() => new Set(pinnedIds), [pinnedIds]);
  const topRows = useMemo(() => rows.filter(r => pinnedSet.has(r.id)), [rows, pinnedSet]);
  const bodyRows = useMemo(() => rows.filter(r => !pinnedSet.has(r.id)), [rows, pinnedSet]);

  const columns = useMemo(() => {
    const isPlayerSportCol = sport === 'ATP' || sport === 'WTA' || sport === 'PGA';
    const baseColumns = [
      {
        field: 'eventName', headerName: '',
        minWidth: isMobile ? (isPlayerSportCol ? 90 : 35) : 230, width: isMobile ? (isPlayerSportCol ? 90 : 35) : undefined,
        flex: 1, sortable: false, disableReorder: true,
        renderCell: (params) => {
          const lastName = (name) => (name || '').split(' ').pop();
          const isPinned = pinnedSet.has(params.row.id);
          return (
          <Box sx={{ ...SX_COL_OUTER, position: 'relative', cursor: 'pointer' }}
            onClick={() => togglePin(params.row.id)}
            title={isPinned ? 'Unpin row' : 'Pin row to top'}>
            <Box className={`pin-btn${isPinned ? ' pinned' : ''}`} sx={{
              position: 'absolute', top: 2, right: 2, display: 'flex', alignItems: 'center',
              color: isPinned ? '#ff1744' : '#888', pointerEvents: 'none',
            }}>
              {isPinned
                ? <PushPinIcon sx={{ fontSize: 13 }} />
                : <PushPinOutlinedIcon sx={{ fontSize: 13 }} />}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', height: '35px', paddingLeft: isMobile ? '4px' : '8px' }}>
              {params.row.awayTeamLogo && (
                <img src={params.row.awayTeamLogo} alt={`${params.row.awayTeam} Logo`}
                  style={{ width: '24px', height: '24px', objectFit: 'contain', marginRight: isMobile ? '0' : '8px' }} />
              )}
              {(!isMobile || isPlayerSportCol) && (
                <Typography
                  onClick={(e) => { e.stopPropagation(); handleTeamClick(params.row, params.row.awayTeam); }}
                  sx={{ fontSize: isMobile ? '11px' : '14px', cursor: 'pointer',
                    '&:hover': { textDecoration: 'underline' } }}>
                  {isMobile ? lastName(params.row.awayTeam) : params.row.awayTeam}
                </Typography>
              )}
            </Box>
            {params.row.isThreeWay && (
              <Box sx={{ display: 'flex', alignItems: 'center', height: '35px', paddingLeft: isMobile ? '4px' : '8px' }}>
                {!isMobile && <Typography sx={{ fontSize: '14px', color: '#888' }}>Draw</Typography>}
              </Box>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', height: '35px', paddingLeft: isMobile ? '4px' : '8px' }}>
              {params.row.homeTeamLogo && (
                <img src={params.row.homeTeamLogo} alt={`${params.row.homeTeam} Logo`}
                  style={{ width: '24px', height: '24px', objectFit: 'contain', marginRight: isMobile ? '0' : '8px' }} />
              )}
              {(!isMobile || isPlayerSportCol) && (
                <Typography
                  onClick={(e) => { e.stopPropagation(); handleTeamClick(params.row, params.row.homeTeam); }}
                  sx={{ fontSize: isMobile ? '11px' : '14px', cursor: 'pointer',
                    '&:hover': { textDecoration: 'underline' } }}>
                  {isMobile ? lastName(params.row.homeTeam) : params.row.homeTeam}
                </Typography>
              )}
            </Box>
          </Box>
          );
        },
      },
      {
        field: 'eventStartFormatted', headerName: '',
        minWidth: isMobile ? 70 : 85, maxWidth: isMobile ? 70 : 85,
        sortable: false, disableReorder: true,
        renderCell: (params) => (
          <Box sx={SX_COL_OUTER}>
            <Box sx={{ height: '35px', display: 'flex', alignItems: 'center', paddingLeft: isMobile ? '0px' : '8px', paddingRight: isMobile ? '4px' : '8px' }}>
              <Typography sx={{ fontSize: isMobile ? '12px' : '14px' }}>{params.value.date}</Typography>
            </Box>
            {params.row.isThreeWay && <Box sx={{ height: '35px' }} />}
            <Box sx={{ height: '35px', display: 'flex', alignItems: 'center', paddingLeft: isMobile ? '0px' : '8px', paddingRight: isMobile ? '4px' : '8px' }}>
              {params.row.isLive ? (
                <Box sx={{
                  display: 'inline-flex', alignItems: 'center', gap: '4px',
                  px: '6px', py: '1px', borderRadius: '4px',
                  backgroundColor: 'rgba(239, 68, 68, 0.15)',
                  border: '1px solid rgba(239, 68, 68, 0.5)',
                }}>
                  <Box sx={{
                    width: '6px', height: '6px', borderRadius: '50%',
                    backgroundColor: '#ef4444',
                    animation: 'pulse 1.5s ease-in-out infinite',
                    '@keyframes pulse': {
                      '0%, 100%': { opacity: 1 },
                      '50%': { opacity: 0.4 },
                    },
                  }} />
                  <Typography sx={{ fontSize: isMobile ? '10px' : '11px', fontWeight: 600, color: '#ef4444', letterSpacing: '0.5px' }}>
                    LIVE
                  </Typography>
                </Box>
              ) : (
                <Typography sx={{ fontSize: isMobile ? '12px' : '14px' }}>{params.value.time}</Typography>
              )}
            </Box>
          </Box>
        ),
      },
      {
        field: 'bestOdds', headerName: 'Best Odds', headerAlign: 'right',
        minWidth: isMobile ? 122 : 155, flex: 1, sortable: false, disableReorder: true,
        renderHeader: () => (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%', paddingRight: isMobile ? '4px' : '8px' }}>
            <Typography sx={{ fontSize: isMobile ? '14px' : '16px', color: '#ffffff' }}>Best Odds</Typography>
          </Box>
        ),
        renderCell: (params) => {
          const value = params.value;
          const m = marketRef.current;
          if (m === 'Summary') {
            return (
              <Box sx={SX_COL_OUTER}>
                <Box sx={{
                  display: 'flex', alignItems: 'center', height: '35px', paddingRight: '8px',
                  cursor: value.topRow?.display ? 'pointer' : 'default',
                  '&:hover': value.topRow?.display ? { backgroundColor: 'rgba(255,255,255,0.05)' } : {},
                }} onClick={() => value.topRow?.meta && openOddsHistory(value.topRow.meta, params.row.bestOddsBookmakers?.topRow)}>
                  <Typography sx={{ fontSize: isMobile ? '12px' : '14px', textAlign: 'right', pr: isMobile ? '4px' : '8px', flex: 1 }}>
                    {value.topRow?.display || ''}
                  </Typography>
                  {value.topRow?.logo && <Tooltip title={params.row.bestOddsBookmakers?.topRow || ''} placement="top" arrow><img src={value.topRow.logo} alt="Best" style={isMobile ? STYLE_BEST_IMG_M : STYLE_BEST_IMG} /></Tooltip>}
                </Box>
                <Box sx={{
                  display: 'flex', alignItems: 'center', height: '35px', paddingRight: '8px',
                  cursor: value.bottomRow?.display ? 'pointer' : 'default',
                  '&:hover': value.bottomRow?.display ? { backgroundColor: 'rgba(255,255,255,0.05)' } : {},
                }} onClick={() => value.bottomRow?.meta && openOddsHistory(value.bottomRow.meta, params.row.bestOddsBookmakers?.bottomRow)}>
                  <Typography sx={{ fontSize: isMobile ? '12px' : '14px', textAlign: 'right', pr: isMobile ? '4px' : '8px', flex: 1 }}>
                    {value.bottomRow?.display || ''}
                  </Typography>
                  {value.bottomRow?.logo && <Tooltip title={params.row.bestOddsBookmakers?.bottomRow || ''} placement="top" arrow><img src={value.bottomRow.logo} alt="Best" style={isMobile ? STYLE_BEST_IMG_M : STYLE_BEST_IMG} /></Tooltip>}
                </Box>
              </Box>
            );
          }
          const outcomeKeys = params.row.outcomeKeys || (m === 'Moneyline' || m === 'Spread' ? ['Away', 'Home'] : ['Over', 'Under']);
          return (
            <Box sx={SX_COL_OUTER}>
              {outcomeKeys.map(outcome => {
                const { display, logo, outcomeName, meta } = value[outcome] || {};
                if (!display) return null;
                return (
                  <Box key={outcome} sx={{
                    display: 'flex', alignItems: 'center', height: '35px', paddingRight: '8px',
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.05)' },
                  }} onClick={() => meta && openOddsHistory(meta, params.row.bestOddsBookmakers?.[outcome])}>
                    <Typography sx={{ fontSize: isMobile ? '12px' : '14px', textAlign: 'right', pr: isMobile ? '4px' : '8px', flex: 1 }}>
                      {display}
                    </Typography>
                    {logo && <Tooltip title={params.row.bestOddsBookmakers?.[outcome] || ''} placement="top" arrow><img src={logo} alt={`${outcomeName} Logo`} style={isMobile ? STYLE_BEST_IMG_M : STYLE_BEST_IMG} /></Tooltip>}
                  </Box>
                );
              })}
            </Box>
          );
        },
      },
    ];

    const visibleBookmakers = (selectedBooks || []).filter(b => availableBooks.includes(b));
    const bookmakerColumns = {};

    visibleBookmakers.forEach(book => {
      bookmakerColumns[book] = {
        field: book, headerName: '', headerAlign: 'right',
        minWidth: isMobile ? 90 : 115, flex: 1, sortable: false, disableReorder: false,
        renderHeader: () => {
          const hasLink = !!bookmakerLinks[book];
          return (
            <Box
              sx={{
                display: 'flex', justifyContent: 'flex-end', alignItems: 'center',
                width: '100%', height: '100%', paddingRight: isMobile ? '4px' : '12px',
                cursor: 'grab', '&:active': { cursor: 'grabbing' }, userSelect: 'none',
              }}
              onClick={(e) => {
                if (hasLink && e.detail > 0) window.open(bookmakerLinks[book], '_blank', 'noopener,noreferrer');
              }}
            >
              {bookLogos[book] ? (
                <Tooltip title={book} placement="bottom" arrow>
                  <img src={bookLogos[book]} alt={book}
                    style={{ maxWidth: isMobile ? '40px' : '60px', maxHeight: isMobile ? '28px' : '36px',
                      width: 'auto', height: 'auto', objectFit: 'contain', pointerEvents: 'none', userSelect: 'none' }} />
                </Tooltip>
              ) : (
                <Typography sx={{ fontSize: isMobile ? '10px' : '12px', color: '#ffffff', textAlign: 'right' }}>{book}</Typography>
              )}
            </Box>
          );
        },
        renderCell: (params) => (
          <BookCell
            bookOdds={params.row.bookmakerOdds && params.row.bookmakerOdds[params.field]}
            best={params.row.bestOddsBookmakers}
            field={params.field}
            market={marketRef.current}
            outcomeKeys={params.row.outcomeKeys}
            showVolume={showVolumeRef.current}
            isMobile={isMobile}
            onOpen={openOddsHistory}
          />
        ),
      };
    });

    const savedOrder = getSavedColumnOrder();
    const finalColumns = [...baseColumns];
    if (savedOrder && savedOrder.bookmakers) {
      savedOrder.bookmakers.forEach(book => { if (bookmakerColumns[book]) finalColumns.push(bookmakerColumns[book]); });
      visibleBookmakers.forEach(book => { if (!savedOrder.bookmakers.includes(book) && bookmakerColumns[book]) finalColumns.push(bookmakerColumns[book]); });
    } else {
      visibleBookmakers.forEach(book => finalColumns.push(bookmakerColumns[book]));
    }
    return finalColumns;
  }, [sport, isMobile, selectedBooks, bookLogos, getSavedColumnOrder, showVolume, pinnedSet, togglePin, handleTeamClick]);

  // Stable row-height fn: an inline lambda forces the grid to re-measure every row on every render
  const getRowHeight = useCallback((params) => (params.model?.isThreeWay ? 105 : 70), []);

  // Subscribe to header drag lifecycle so data flushes pause during a reorder.
  const gridMounted = rows.length > 0;
  useEffect(() => {
    const api = apiRef.current;
    if (!gridMounted || !api || typeof api.subscribeEvent !== 'function') return undefined;
    let safety = null;
    const root = api.rootElementRef?.current;
    const endDrag = () => {
      if (safety) { clearTimeout(safety); safety = null; }
      if (!dragActiveRef.current) return;
      dragActiveRef.current = false;
      if (root) root.classList.remove('ss-col-dragging');
      if (dragPendingRef.current) {
        dragPendingRef.current = false;
        dirtyRef.current = false;
        setThrottledBets(latestBetsRef.current());
      }
    };
    const startDrag = () => {
      dragActiveRef.current = true;
      if (root) root.classList.add('ss-col-dragging');
      // Safety valve: never hold updates longer than 15s if dragend is lost
      if (safety) clearTimeout(safety);
      safety = setTimeout(endDrag, 15000);
    };
    // Also hold updates from pointer-down on a header: the browser needs a few
    // quiet frames to initiate a native drag, and a data flush in that window
    // (or a long render already in flight) prevents the drag from ever starting.
    const onPointerDown = (e) => {
      if (e.target && e.target.closest && e.target.closest('.MuiDataGrid-columnHeader')) startDrag();
    };
    const onPointerUp = () => { setTimeout(endDrag, 0); };
    if (root) root.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('pointerup', onPointerUp);
    const unsubs = [
      api.subscribeEvent('columnHeaderDragStart', startDrag),
      api.subscribeEvent('columnHeaderDragEnd', endDrag),
    ];
    return () => {
      if (root) root.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('pointerup', onPointerUp);
      unsubs.forEach(u => { try { u(); } catch (e) { /* ignore */ } });
      endDrag();
    };
  }, [apiRef, gridMounted]);

  const handleColumnOrderChange = useCallback(() => {
    if (!apiRef.current) return;
    try {
      const orderedFields = apiRef.current.getAllColumns().map(c => c.field);
      const bookmakers = orderedFields.filter(f => !FIXED_COLUMNS.includes(f));
      saveColumnOrder({ fixed: FIXED_COLUMNS, bookmakers });
    } catch (e) { /* ignore */ }
  }, [apiRef, saveColumnOrder]);

  // ══════════════════════════════════════════════════════════
  //  RENDER
  // ══════════════════════════════════════════════════════════

  // ── Filter controls (shared between mobile & desktop layouts) ──
  const leagueSelect = (
    <FormControl variant="outlined" size="small" sx={{ minWidth: isMobile ? 0 : 120, flex: isMobile ? 1 : 'none' }}>
      <InputLabel id="sport-filter-label" shrink sx={{ color: '#ffffff' }}>League</InputLabel>
      <Select labelId="sport-filter-label" label="League" notched value={sport} onChange={(e) => setSport(e.target.value)}>
        {leagueOptions.map(league => (
          <MenuItem key={league} value={league}>{league}</MenuItem>
        ))}
      </Select>
    </FormControl>
  );
  const marketSelect = (
    <FormControl variant="outlined" size="small" sx={{ minWidth: isMobile ? 0 : 120, flex: isMobile ? 1 : 'none' }}>
      <InputLabel id="market-filter-label" shrink sx={{ color: '#ffffff' }}>Market</InputLabel>
      <Select labelId="market-filter-label" label="Market" notched value={market} onChange={(e) => setMarket(e.target.value)}>
        {marketOptions.map(type => (<MenuItem key={type} value={type}>{type}</MenuItem>))}
      </Select>
    </FormControl>
  );
  const periodSelect = (
    <FormControl variant="outlined" size="small" sx={{ minWidth: isMobile ? 0 : 120, flex: isMobile ? 1 : 'none' }}>
      <InputLabel id="period-type-filter-label" shrink sx={{ color: '#ffffff' }}>Period Type</InputLabel>
      <Select labelId="period-type-filter-label" label="Period Type" notched value={periodType} onChange={(e) => setPeriodType(e.target.value)}>
        {periodTypes.map(type => (<MenuItem key={type} value={type}>{type}</MenuItem>))}
      </Select>
    </FormControl>
  );
  const booksSelect = (
    <FormControl variant="outlined" size="small" sx={{ minWidth: isMobile ? 0 : 140, flex: isMobile ? 1 : 'none' }}>
      <InputLabel id="books-filter-label" shrink sx={{ color: '#ffffff' }}>Books</InputLabel>
      <Select multiple labelId="books-filter-label" label="Books" notched
        value={selectedBooks || []} onChange={handleBooksFilterChange} renderValue={renderBooksValue}
        MenuProps={{ autoFocus: false, disableAutoFocusItem: true, PaperProps: { style: { maxHeight: 320 } } }}>
        <MenuItem value="all">
          <Checkbox checked={availableBooks.length > 0 && (selectedBooks || []).length === availableBooks.length} />
          <ListItemText primary="Select All" />
        </MenuItem>
        {/* Combo presets — pick exactly the books of one category */}
        {BOOK_GROUPS.map(g => {
          const matches = booksInGroup(g, availableBooks);
          if (matches.length === 0) return null;
          return (
            <MenuItem key={g.label} value={GROUP_PREFIX + g.label} dense sx={{ borderBottom: g.label === "Pick'ems" ? '1px solid #444' : 'none' }}>
              <ListItemText
                primary={`${g.label} (${matches.length})`}
                sx={{ '& .MuiTypography-root': { fontSize: '0.8rem', fontStyle: 'italic', color: '#ff8a80' } }}
              />
            </MenuItem>
          );
        })}
        {availableBooks.map((book) => (
          <MenuItem key={book} value={book}>
            <Checkbox checked={(selectedBooks || []).includes(book)} />
            <ListItemText primary={book} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
  const searchField = (
    <TextField
      value={searchText} onChange={(e) => setSearchText(e.target.value)}
      placeholder="Search team" size="small" disabled={!isAuthenticated}
      InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#BBBBBB' }} /></InputAdornment> }}
      sx={{
        width: isMobile ? 'auto' : 200, flex: isMobile ? 1 : 'none', minWidth: 0,
        '& .MuiInputBase-input': { color: '#ffffff' },
        '& .MuiInputBase-input::placeholder': { color: '#BBBBBB', opacity: 1 },
      }}
    />
  );
  const volCheck = (
    <FormControlLabel
      control={<Checkbox checked={showVolume} onChange={() => setShowVolume(v => !v)} sx={{ color: '#ffffff', '&.Mui-checked': { color: '#ff1744' }, p: '4px' }} />}
      label={<Typography sx={{ color: '#ffffff', fontSize: '0.875rem' }}>Vol</Typography>}
      sx={{ ml: 0, mr: 0 }}
    />
  );
  const liveCheck = (
    <FormControlLabel
      control={<Checkbox checked={liveVisible} disabled={!isAuthenticated} onChange={() => setShowLive(v => !v)} sx={{ color: '#ffffff', '&.Mui-checked': { color: '#ff1744' }, '&.Mui-disabled': { color: '#555555' }, p: '4px' }} />}
      label={<Typography sx={{ color: isAuthenticated ? '#ffffff' : '#777777', fontSize: '0.875rem', whiteSpace: 'nowrap' }}>{isMobile ? 'Live' : 'Show Live'}</Typography>}
      sx={{ ml: 0, mr: 0 }}
    />
  );

  return (
    <ThemeProvider theme={customTheme}>
      <StyledEngineProvider injectFirst>
        <CssBaseline />
        <Box sx={{ width: '100%', height: 'calc(100vh - 64px)', display: 'flex', flexDirection: 'column', overflow: 'hidden', backgroundColor: 'background.default' }}>
          <Box className="filter-section" sx={{ padding: isMobile ? '8px' : '16px' }}>
            {isMobile ? (
              <Stack direction="column" spacing={0.75}>
                {/* Always visible: search + filter toggle */}
                <Stack direction="row" spacing={0.5} alignItems="center">
                  {searchField}
                  <IconButton
                    onClick={() => setMobileFiltersOpen(o => !o)}
                    sx={{
                      color: mobileFiltersOpen ? '#ff1744' : '#ffffff',
                      border: '1px solid #555', borderRadius: 1, p: '6px', flexShrink: 0,
                    }}
                  >
                    <FilterListIcon sx={{ fontSize: '1.2rem' }} />
                  </IconButton>
                </Stack>
                {/* Collapsible: dropdowns + checkboxes */}
                <Collapse in={mobileFiltersOpen}>
                  <Stack direction="column" spacing={0.75}>
                    <Stack direction="row" spacing={0.75}>
                      {leagueSelect}
                      {marketSelect}
                    </Stack>
                    <Stack direction="row" spacing={0.75}>
                      {periodSelect}
                      {booksSelect}
                    </Stack>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      {volCheck}
                      {liveCheck}
                    </Stack>
                  </Stack>
                </Collapse>
              </Stack>
            ) : (
              <Stack direction="row" spacing={2} alignItems="center">
                {leagueSelect}
                {marketSelect}
                {periodSelect}
                {booksSelect}
                {searchField}
                {volCheck}
                {liveCheck}
              </Stack>
            )}
          </Box>
          <Box className="table-section" sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
            <Box sx={{ width: '100%', height: '100%' }}>
              {rows.length === 0 ? (
                <Box className="empty-state" sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <Typography variant="body1">No odds data available for the selected filters.</Typography>
                </Box>
              ) : (
                <Box sx={{ height: '100%', width: '100%' }}>
                  <DataGridPro
                    apiRef={apiRef} rows={bodyRows} columns={columns}
                    pinnedRows={{ top: topRows }}
                    pinnedColumns={{ left: ['eventName'] }}
                    disableSelectionOnClick disableRowSelectionOnClick
                    disableColumnSort disableColumnMenu disableColumnSelector disableColumnFilter
                    getRowHeight={getRowHeight}
                    keepColumnPositionIfDraggedOutside
                    onColumnOrderChange={handleColumnOrderChange}
                    sx={DATA_GRID_SX}
                    rowBuffer={5} rowThreshold={2}
                  />
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </StyledEngineProvider>

      <MarketsModal
        open={marketsOpen}
        onClose={closeMarkets}
        markets={marketsData}
        meta={marketsMeta}
      />
      <OddsHistoryModal
        open={historyOpen}
        onClose={closeOddsHistory}
        data={historyData}
        loading={historyLoading}
        meta={historyMeta}
      />
    </ThemeProvider>
  );
};

export default Odds;